import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import './AdminTable.scss'
import Input from './Input.js'
import { use } from '../Helpers.js'
import { FaLessThanEqual, FaFlag, FaRegFlag } from 'react-icons/fa6'

const FLAG_COLORS = [null, 'red', 'green', 'blue']

function Table ({ tableSettings, updateTable = false }) {
  const {
    columns,
    data = [],
    actions = {},
    headerContent = null,
    addNew = { enabled: false, url: '/' },
    filters = false,
    pagination = false,
    rowCountInfo = true,
    rowNumbers = true,
    editLink = null
  } = tableSettings

  const [tableData, setTableData] = useState([])
  const [filterRowOpen, setFilterRowOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [showRowMenu, setShowRowMenu] = useState(false)
  const navigate = useNavigate()

  const [paginationParams, setPaginationParams] = useState({
    pageSize: 30,
    pageNumber: 1,
    sort: 'asc'
  })

  const [filterParams, setFilterParams] = useState({})

  const [queryParams, setQueryParams] = useState(
    pagination ? { ...paginationParams, ...filterParams } : { ...filterParams }
  )

  const [paginationResults, setPaginationResults] = useState({
    totalRecords: 0,
    totalPages: 0
  })

  useEffect(() => {
    if (pagination) {
      setQueryParams({ ...filterParams, ...paginationParams })
    } else {
      setQueryParams({ ...filterParams })
    }
  }, [filterParams, paginationParams])

  const getData = async () => {
    setLoading(true)
    let _data = []
    if (actions.get) {
      _data = await actions.get(queryParams)
    }
    if (data.length != 0) {
      _data = data
    }
    if (pagination) {
      setPaginationResults({
        totalRecords: _data[0]?.totalRecords,
        totalPages: _data[0]?.totalPages
      })
    }
    setTableData(_data)
    //setTimeout(()=>{setLoading(false)},1000)
    setLoading(false)
  }

  useEffect(() => {
    if (updateTable) {
      getData()
    }
  }, [updateTable])

  useEffect(() => {
    getData()
  }, [queryParams])

  const changeUserSettings = async (key, value) => {
    pagination.settings[key] = value
  }

  const collectFilterParams = () => {
    let _filterParams = {}
    columns
      .filter(c => c.filter?.enabled)
      .forEach(c => {
        _filterParams[c.filter?.name] = use.getInputValueByName(
          c.filter?.name,
          false,
          document.getElementById('filter-row')
        )
      })
    setFilterParams(_filterParams)
    setPaginationParams({ ...paginationParams, pageNumber: 1 })
  }

  const resetFilterParams = () => {
    columns
      .filter(c => c.filter?.enabled)
      .forEach(c => {
        use.resetInputValueByName(
          c.filter?.name,
          false,
          document.getElementById('filter-row')
        )
      })
    setFilterParams({})
    setPaginationParams({ ...paginationParams, pageNumber: 1 })
  }

const formCommandObject = (operation, rowData) => {
    let parameters = {},
      disabled = false
    let commandParams = new URLSearchParams({
      requestType: 'command',
      commandName: operation.name,
      warning: operation.warning,
      parameters: JSON.stringify(operation.parameters),
      token: 'xy103'
    })

    disabled = operation.disablingConditions?.some(dc =>
      dc.checkCondition(rowData[dc.fieldName])
    )

    return { commandParams, disabled }
  }

  const getRowEditTarget = rowData => {
    if (typeof editLink != 'function') return null
    return editLink(rowData)?.toString()
  }

  return (
    <div className='admin-table'>
      <div className='actions-header'>
        <div className='left-side'>
          {addNew && addNew.enabled && (
            <Link to={addNew.url} className='add-new'>
              <span className='plus-icon'>+</span>Əlavə et
            </Link>
          )}
        </div>
        <div className='right-side'>
          {rowCountInfo && (
            <span className='record-count'>
              Say: {paginationResults.totalRecords || 0}
            </span>
          )}

          {filters && (
            <span
              className='filter'
              onClick={() => setFilterRowOpen(state => !filterRowOpen)}
            >
              <img
                className={`arrow${filterRowOpen ? ' rotated' : ''}`}
                src='/images/icons/down-arrow.png'
              />
              Axtarış
              <img className='search' src='/images/icons/search.png' />
            </span>
          )}
        </div>
      </div>
      <table className='admin-data'>
        <thead>
          <tr>
            {columns.map((column, index) => {
              return (
                <th
                  key={index + 1}
                  title={column.description}
                  style={column.style}
                >
                  {column.title}
                </th>
              )
            })}
          </tr>
          {filters && (
            <tr
              style={filterRowOpen ? {} : { display: 'none' }}
              className='filter-row'
              id='filter-row'
            >
              {columns.map((column, index) => {
                return (
                  <td key={index + 1}>
                    {column?.filter != null && (
                      <Input
                        className='filter-input'
                        type={column.filter.type}
                        disabled={!column.filter.enabled}
                        fieldName={column.filter.name}
                        placeholder={!column.filter.enabled ? '-' : ''}
                        defaultSelectOptions={
                          column.filter.type == 'select'
                            ? [{ value: '', text: '(Bütün)' }]
                            : []
                        }
                        value={filterParams[column.filter?.name]}
                        data={column.filter.data}
                        dataSource={column.filter.dataSource}
                      />
                    )}
                    {column.type == 'operations-list' ? (
                      <div className='filter-actions'>
                        <Input
                          type={'button'}
                          label={'Sorğula'}
                          eventHandler={[
                            {
                              type: 'click',
                              handler: () => collectFilterParams()
                            }
                          ]}
                          col={{ sm: 7 }}
                          className={'action get'}
                        />
                        <Input
                          type={'button'}
                          label={'Sıfırla'}
                          eventHandler={[
                            {
                              type: 'click',
                              handler: () => resetFilterParams()
                            }
                          ]}
                          col={{ sm: 5 }}
                          className={'action reset'}
                        />
                      </div>
                    ) : null}
                  </td>
                )
              })}
            </tr>
          )}
          {headerContent && (
            <tr>
              <th className='table-sub-header' colSpan={columns.length}>
                {headerContent}
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {tableData &&
            tableData?.length > 0 &&
            tableData?.map((rowData, index) => {
              return (
                <tr
                  key={index + 1}
                  onClick={e => {
                    if (e.target.closest('.operations-list')) return
                    const target = getRowEditTarget(rowData)
                    if (target) {
                      navigate(target)
                    }
                  }}
                >
                  {columns.map((column, index) => {
                    let key = index + 1
                    let value = null;
                    if (Array.isArray(column.property)) {
                      value = new Array()
                      column.property.forEach(p => {
                        value.push(rowData[p])
                        if (!column.mutate && column.type!='operations-list') {
                          value = value.join(',')
                        }
                      })
                    } else {
                      value = rowData[column.property]
                    }

                    if (column.type != 'operations-list' && column.mutate) {
                      value = column.mutate(value)
                    }

                    switch (column.type) {
                      case 'operations-list':
                        let operationsList = column.operations(value)
                        return (
                          <td className={column.type} key={key}>
                            <div className='operations-container'>
                              {operationsList?.map((operation, idx) => {
                                let cObj = {}
                                switch (operation.type) {
                                  case 'group':
                                    return (
                                      <RowActionsGroupMenu
                                        key = {`${operation.name}#${idx}`}
                                        actionParameters={operation}
                                        rowData={rowData}
                                      />
                                    )
                                  case 'link':
                                    cObj = formCommandObject(operation, rowData)
                                    return (
                                      <Link
                                        disabled={cObj.disabled}
                                        className={
                                          cObj.disabled ? 'disabled' : ''
                                        }
                                        key={idx}
                                        to={cObj.disabled ? '' : operation.link.toString()}
                                      >
                                        {operation.iconType == 'path' ? (
                                          <img
                                            className='operation-icon'
                                            src={operation.icon}
                                          />
                                        ) : (
                                          `${operation.icon}`
                                        )}
                                      </Link>
                                    )
                                  case 'command':
                                    cObj = formCommandObject(operation, rowData)
                                    return (
                                      <Link
                                        key={idx}
                                        disabled={cObj.disabled}
                                        className={
                                          cObj.disabled ? 'disabled' : ''
                                        }
                                        onClick={e => {
                                          e.preventDefault()
                                          setSearchParams(
                                            cObj.disabled
                                              ? {}
                                              : cObj.commandParams
                                          )
                                        }}
                                      >
                                        {operation.iconType == 'path' ? (
                                          <img
                                            style={operation.iconStyle}
                                            className='operation-icon'
                                            src={operation.icon}
                                          />
                                        ) : (
                                          <span
                                            className='operation-icon-text'
                                            style={operation.iconStyle}
                                          >
                                            {operation.icon}
                                          </span>
                                        )}
                                      </Link>
                                    )
                                  default:
                                    return <></>
                                }
                              })}
                            </div>
                          </td>
                        )
                      case 'image':
                        return (
                          <td key={key}>
                            <img src={value} />
                          </td>
                        )
                      case 'label':
                        return (
                          <td className='label' key={key}>
                            {value}
                          </td>
                        )
                      case 'labels':
                        return (
                          <td className='labels' key={key}>
                            {value?.map((v, index) => (
                              <span key={v + '#' + index} className='label'>
                                {v}
                              </span>
                            ))}
                          </td>
                        )
                      case 'file':
                        return value ? (
                          <td className='file' key={key}>
                            {value}
                          </td>
                        ) : (
                          <td></td>
                        )
                      case 'checkbox':
                        return (
                          <td key={key}>
                            <input
                              type='checkbox'
                              name={column.property + '[]'}
                              readOnly
                              checked={value}
                              value={value}
                            />
                          </td>
                        )
                      case 'number':
                        return (
                          <td className='number' key={key}>
                            <span>{value}</span>
                          </td>
                        )
                      case 'status-label':
                        return (
                          <td className='status-label' key={key}>
                            <span>{value}</span>
                          </td>
                        )
                      case 'flag':
                        return (
                          <td
                            className='flag'
                            key={key}
                            onClick={async e => {
                              e.stopPropagation()
                              const nextColor =
                                FLAG_COLORS[
                                  (FLAG_COLORS.indexOf(value) + 1) %
                                    FLAG_COLORS.length
                                ]
                              const updated = await column.onUpdate?.(
                                rowData,
                                nextColor
                              )
                              if (updated) {
                                setTableData(prev =>
                                  prev.map(r =>
                                    r.id === rowData.id
                                      ? { ...r, [column.property]: nextColor }
                                      : r
                                  )
                                )
                              }
                            }}
                          >
                            {value ? (
                              <FaFlag className={`flag-icon ${value}`} />
                            ) : (
                              <FaRegFlag className='flag-icon none' />
                            )}
                          </td>
                        )
                      default:
                        return (
                          <td style={column.style?.rows} key={key}>
                            {value}
                          </td>
                        )
                    }
                  })}
                </tr>
              )
            })}
        </tbody>
      </table>
      {loading && <div className='loading'>Yüklənir...</div>}
      {!loading && tableData && tableData.length === 0 && (
        <div className='no-data'>Məlumat yoxdur</div>
      )}
      <div className='table-footer'>
        {!loading &&
          pagination &&
          tableData &&
          tableData.length > 0 &&
          paginationResults.totalPages > 1 && (
            <div className='pagination-wrapper'>
              <div className='page-size'>
                <label>Sətirlər: </label>
                <select
                  value={paginationParams?.pageSize}
                  onChange={e => {
                    changeUserSettings('pageSize', +e.target.value)
                  }}
                >
                  <option value='10'>10</option>
                  <option value='20'>25</option>
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                </select>
              </div>
              <div className='page-actions'>
                <button
                  className='navigation-button previous-page'
                  onClick={() => {
                    if (paginationParams.pageNumber - 1 < 1) {
                      return false
                    } else {
                      setPaginationParams({
                        ...paginationParams,
                        pageNumber: paginationParams.pageNumber - 1
                      })
                    }
                  }}
                >
                  <span>{'<'}</span>
                </button>
                <div className='page-number'>
                  <input
                    type='number'
                    className='current-page'
                    defaultValue={paginationParams.pageNumber}
                    onKeyUp={e => {
                      if (e.key == 'Enter') {
                        let goToPage = Number(e.target.value)
                        if ([null, ''].includes(goToPage)) {
                          return false
                        }
                        if (
                          goToPage > 0 &&
                          goToPage <= paginationResults.totalPages
                        ) {
                          setPaginationParams({
                            ...paginationParams,
                            pageNumber: goToPage
                          })
                        } else {
                          alert('Daxil etdiyiniz dəyər etibarlı deyil.')
                        }
                      }
                    }}
                  />{' '}
                  / {paginationResults.totalPages}
                </div>
                <button
                  className='navigation-button next-page'
                  onClick={() => {
                    if (
                      paginationParams.pageNumber + 1 >
                      paginationResults.totalPages
                    ) {
                      return false
                    } else {
                      setPaginationParams({
                        ...paginationParams,
                        pageNumber: paginationParams.pageNumber + 1
                      })
                    }
                  }}
                >
                  <span>{'>'}</span>
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

function RowActionsGroupMenu ({actionParameters, rowData}) {
  const [showRowMenu,setShowRowMenu] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams()

  const formCommandObject = (operation, rowData) => {
    let disabled = false;

    let commandParams = new URLSearchParams({
      requestType: 'command',
      commandName: operation.name,
      warning: operation.warning,
      parameters: operation.parameters,
      token: 'xy103'
    })

    disabled = operation.disablingConditions?.some(dc =>
      dc.checkCondition(rowData[dc.fieldName])
    )

    return { commandParams, disabled }
  }

  return (
    <div className={`operation-group ${showRowMenu ? 'show' : ''}`} style={{ position: 'relative' }}>
      <Link
        onClick={e => {
          e.preventDefault()
          setShowRowMenu(!showRowMenu)
        }}
        onBlur={()=>setShowRowMenu(false)}
      >
        <span className='operation-icon-text'>{actionParameters?.icon}</span>
         <ul className={`row-menu`}>
        {actionParameters.items.map(groupItem => {
          let cObj = formCommandObject(groupItem, rowData)
          return (
            <li
              key={groupItem.name}
              style={groupItem.name == 'delete' ? { color: '#e11e1e' } : {}}
              className={cObj.disabled? 'disabled':''}
              onClick={cObj.disabled? e=> false: e => {
                e.preventDefault()
                let newSearchParams = cObj.disabled ? null : cObj.commandParams
                setSearchParams(newSearchParams)
              }}
            >
              {groupItem.title}
            </li>
          )
        })}
      </ul>
      </Link>
     
    </div>
  )
}

export default Table
